import type { ReactNode } from 'react';

/** Sets the border and progress colour of a toast. */
export type ToastKind = 'info' | 'success' | 'warning' | 'error';

/** Where toasts appear. Only the corner the portal uses is drawn. */
export type ToastPosition = 'top-right';

export interface ToastRequest {
    /** A toast with the same id that is still open is not shown twice. */
    id: string;
    content: ReactNode;
    kind: ToastKind;
    icon: ReactNode;
    /** Milliseconds before the toast closes itself, or `false` to stay until closed. */
    autoClose: number | false;
    closeOnClick: boolean;
    /** Draws the bar that runs down while an `autoClose` toast counts down. */
    showProgress: boolean;
    /**
     * A route segment the toast belongs to, such as `lice-report`: the toast closes when the
     * route stops containing it. Without one the toast outlives navigation.
     */
    page?: string;
    /** Called once, when the toast closes for any reason. */
    onClose?: () => void;
}

export interface ToastEntry extends ToastRequest {
    /** Identifies this showing of the toast; `id` repeats when a closed toast is shown again. */
    key: number;
    open: boolean;
}

// Long enough for the exit animation to finish before the closed toast leaves the list.
const REMOVE_AFTER_CLOSE_MS = 1000;

let entries: ToastEntry[] = [];
let nextKey = 1;
const listeners = new Set<() => void>();

function publish(next: ToastEntry[]) {
    entries = next;
    for (const listener of listeners) listener();
}

/**
 * Queues a toast. Kept outside React so plain functions (MQTT handlers, mutation callbacks)
 * can raise one; the Toaster renders whatever is queued, including toasts raised before it
 * mounted. Returns false when an open toast already has the id.
 */
export function showToast(request: ToastRequest): boolean {
    if (entries.some((entry) => entry.open && entry.id === request.id)) return false;
    publish([...entries, { ...request, key: nextKey++, open: true }]);
    return true;
}

function close(match: (entry: ToastEntry) => boolean) {
    const closing = entries.filter((entry) => entry.open && match(entry));
    if (closing.length === 0) return;
    publish(entries.map((entry) => (closing.includes(entry) ? { ...entry, open: false } : entry)));
    for (const entry of closing) {
        entry.onClose?.();
        setTimeout(() => {
            publish(entries.filter((current) => current.key !== entry.key));
        }, REMOVE_AFTER_CLOSE_MS);
    }
}

/** Closes the open toast with this id, or every open toast when no id is given. */
export function dismissToast(id?: string): void {
    close((entry) => id === undefined || entry.id === id);
}

/** Closes one showing of a toast, as the Toaster does when Radix closes it. */
export function closeToastEntry(key: number): void {
    close((entry) => entry.key === key);
}

/** Closes the toasts that belong to a page the route has left. */
export function dismissToastsOffPage(pathname: string): void {
    const segments = pathname.split('/');
    close((entry) => entry.page !== undefined && !segments.includes(entry.page));
}

export function subscribeToToasts(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function getToasts(): ToastEntry[] {
    return entries;
}
